import { useMemo } from 'react';

import Loading from '../../shared/Loading';
import { TocaIcon, TocaIconMap } from '../model/components/TocaIcon';
import {
    isMultiChoicePollAlternative,
    isThisOrThatPollAlternative,
    isVoteSliderPollAlternative,
    MultiChoicePollAlternative,
    PollAlternative,
    PortalPollType,
    ThisOrThatPollAlternative,
    UnityColor,
    unityColorToHex,
    VoteSliderPollAlternative,
} from '@lifeserver/shared';

import { PollResultDto, usePollResult } from '../../hooks/portal';

import { assert } from '../../utils/assert';

import styles from './PortalPollResults.module.scss';

export interface PortalPollResultsProps {
    pollId: string;
    alternatives: PollAlternative[];
    pollType: PortalPollType;
    className?: string;
}

export const PortalPollResults = ({
    pollId,
    alternatives,
    pollType,
    className,
}: PortalPollResultsProps) => {
    const { data: pollResult, isLoading, error } = usePollResult(pollId);

    if (isLoading) {
        return <Loading />;
    }

    if (error) {
        return (
            <div className={`${styles.error} ${className || ''}`}>
                <p>Failed to load poll results</p>
                <p className={styles.errorMessage}>
                    {error instanceof Error ? error.message : 'Unknown error'}
                </p>
            </div>
        );
    }

    if (!pollResult) {
        return (
            <div className={`${styles.noData} ${className || ''}`}>
                <p>No poll results available</p>
            </div>
        );
    }

    return (
        <div className={`${styles.container} ${className || ''}`}>
            <PortalPollResultsDisplay
                pollResult={pollResult}
                alternatives={alternatives}
                pollType={pollType}
            />
        </div>
    );
};

export interface PortalPollResultsDisplayProps {
    pollResult: PollResultDto;
    alternatives: PollAlternative[];
    pollType: PortalPollType;
}

export const PortalPollResultsDisplay = ({
    pollResult,
    alternatives,
    pollType,
}: PortalPollResultsDisplayProps) => {
    const getResultsForType = (type: PortalPollType) => {
        switch (type) {
            case 'VoteSlider':
                assert(alternatives.every(isVoteSliderPollAlternative));
                return (
                    <VoteSliderResultsDisplay
                        pollResult={pollResult}
                        alternatives={alternatives}
                    />
                );
            case 'MultiChoice':
                assert(alternatives.every(isMultiChoicePollAlternative));
                return (
                    <ManyAlternativesResultsDisplay
                        pollResult={pollResult}
                        alternatives={alternatives}
                    />
                );
            case 'ThisOrThat':
                assert(alternatives.every(isThisOrThatPollAlternative));
                return (
                    <ManyAlternativesResultsDisplay
                        pollResult={pollResult}
                        alternatives={alternatives}
                    />
                );
            default:
                return (
                    <div className={styles.noData}>
                        <p>Result component not implemented for {pollType}</p>
                    </div>
                );
        }
    };

    return (
        <div className={styles.resultsDisplay}>
            {getResultsForType(pollType)}
        </div>
    );
};

const VoteSliderResultsDisplay = ({
    pollResult,
    alternatives,
}: {
    pollResult: PollResultDto;
    alternatives: VoteSliderPollAlternative[];
}) => {
    const result = pollResult.results?.[0]; // VoteSlider should have only one result
    const alternative = alternatives?.[0]; // VoteSlider should have only one alternative

    if (!result || !alternative) {
        return (
            <div className={styles.noData}>
                <p>No vote slider results available</p>
            </div>
        );
    }

    const percentage = Math.round(result.result);

    return (
        <div className={styles.voteSliderResults}>
            <div className={styles.voteSliderBar}>
                <div
                    className={styles.voteSliderProgress}
                    style={{ width: `${percentage}%` }}
                >
                    {alternative.iconName && (
                        <div className={styles.voteSliderIcon}>
                            <img
                                src={TocaIconMap[alternative.iconName]}
                                alt="Vote Slider Icon"
                                className={styles.voteSliderIconImage}
                            />
                        </div>
                    )}
                </div>
                <div className={styles.voteSliderPercentage}>{percentage}%</div>
            </div>
        </div>
    );
};

const ManyAlternativesResultsDisplay = ({
    pollResult,
    alternatives,
}: {
    pollResult: PollResultDto;
    alternatives: ThisOrThatPollAlternative[] | MultiChoicePollAlternative[];
}) => {
    const totalVotes = useMemo(() => {
        if (!pollResult.results) return 0;
        return pollResult.results.reduce((sum, item) => sum + item.result, 0);
    }, [pollResult.results]);

    const results = useMemo(() => {
        if (!pollResult.results) return [];

        return pollResult.results
            .map((item) => {
                const alternativeIndex = item.alternative;
                const alternative = alternatives?.[alternativeIndex];

                return {
                    alternativeIndex,
                    count: item.result,
                    icon: alternative?.iconName,
                    url: isMultiChoicePollAlternative(alternative)
                        ? alternative.url
                        : undefined,
                    backgroundColor: alternative?.backgroundColor,
                };
            })
            .sort((a, b) => b.count - a.count);
    }, [pollResult.results, alternatives]);

    const getPercentage = (count: number) => {
        if (totalVotes === 0) return 0;
        return Math.round((count / totalVotes) * 100);
    };

    const getAlternativeDisplay = (
        alternativeIndex: number,
        icon?: TocaIcon,
        url?: string,
        backgroundColor?: UnityColor,
    ) => {
        if (url) {
            return (
                <div className={styles.imageAlternative}>
                    <img
                        src={url.replace('{0}', '@1x')}
                        alt={`Alternative ${alternativeIndex + 1}`}
                        className={styles.alternativeImage}
                    />
                </div>
            );
        }

        if (icon) {
            return (
                <div className={styles.iconAlternative}>
                    <img
                        src={TocaIconMap[icon]}
                        alt={`Alternative ${alternativeIndex + 1}`}
                        className={styles.alternativeIcon}
                    />
                </div>
            );
        }

        if (backgroundColor) {
            return (
                <div className={styles.colorAlternative}>
                    <div
                        className={styles.colorSwatch}
                        style={{
                            backgroundColor: unityColorToHex(backgroundColor),
                        }}
                    />
                    <span>Color {alternativeIndex + 1}</span>
                </div>
            );
        }
        return <span>Alternative {alternativeIndex + 1}</span>;
    };

    return (
        <div className={styles.results}>
            {results.map(
                ({ alternativeIndex, count, icon, url, backgroundColor }) => {
                    const percentage = getPercentage(count);
                    return (
                        <div
                            key={alternativeIndex}
                            className={styles.resultItem}
                        >
                            <div className={styles.resultHeader}>
                                <div className={styles.alternative}>
                                    {getAlternativeDisplay(
                                        alternativeIndex,
                                        icon,
                                        url ?? undefined,
                                        backgroundColor,
                                    )}
                                </div>
                                <div className={styles.stats}>
                                    <span className={styles.count}>
                                        {count}
                                    </span>
                                    <span className={styles.percentage}>
                                        ({percentage}%)
                                    </span>
                                </div>
                            </div>
                            <div className={styles.progressBar}>
                                <div
                                    className={styles.progress}
                                    style={{ width: `${percentage}%` }}
                                />
                            </div>
                        </div>
                    );
                },
            )}
        </div>
    );
};
